import React, { useState } from 'react';

import { Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './PolicyPage.module.css';
import ContactPage from '../ContactPage/ContactPage';



const socalCountyRentals = [
  {
    id: 'los-angeles-county',
    name: 'Los Angeles County',
    mapLabel: 'LA',
    bounds: '34.82,-117.65,33.70,-118.95',
    region: 'Coastal + lake rentals',
    points: '115,95 240,85 265,160 215,225 125,205 85,145',
    labelX: 168,
    labelY: 152,
    summary:
      'Los Angeles County is best for renters planning lake days, harbor trips, group boat rentals, and jet ski rentals near the larger Los Angeles area.',
    knownFor:
      'Known for flexible water rental demand, weekend lake trips, harbor access, and group-friendly boat rentals.',
    areas: ['Castaic Lake', 'Pyramid Lake', 'Marina / harbor trips', 'Weekend water rentals'],
    popular: [
      { label: 'Boats', keyword: 'boat' },
      { label: 'Jet Skis', keyword: 'jet ski' },
    ],
  },
  {
    id: 'orange-county',
    name: 'Orange County',
    mapLabel: 'OC',
    bounds: '33.95,-117.41,33.33,-118.12',
    region: 'Coastal water rentals',
    points: '215,225 285,205 315,260 280,315 220,300 195,260',
    labelX: 255,
    labelY: 263,
    summary:
      'Orange County is best for coastal renters searching for boats, jet skis, harbor cruises, and beach-area weekend rentals.',
    knownFor:
      'Known for premium coastal water trips, harbor-style rentals, jet ski days, and family-friendly boat bookings.',
    areas: ['Newport area', 'Dana Point area', 'Coastal trips', 'Jet ski days'],
    popular: [
      { label: 'Boats', keyword: 'boat' },
      { label: 'Jet Skis', keyword: 'jet ski' },
    ],
  },
  {
    id: 'riverside-county',
    name: 'Riverside County',
    mapLabel: 'RIV',
    bounds: '34.08,-114.43,33.42,-117.68',
    region: 'Lake + desert rentals',
    points: '265,160 430,150 455,255 405,335 280,315 315,260 285,205',
    labelX: 365,
    labelY: 240,
    summary:
      'Riverside County works well for renters looking for Lake Perris-style water rentals plus inland desert access for UTV trips.',
    knownFor:
      'Known for mixed rental demand: lake days, inland family trips, desert weekends, jet skis, boats, and UTVs.',
    areas: ['Lake Perris', 'Inland Empire', 'Desert weekend trips', 'Family lake days'],
    popular: [
      { label: 'Jet Skis', keyword: 'jet ski' },
      { label: 'Boats', keyword: 'boat' },
      { label: 'UTVs', keyword: 'utv' },
    ],
  },
  {
    id: 'san-bernardino-county',
    name: 'San Bernardino County',
    mapLabel: 'SB',
    bounds: '35.80,-114.13,33.86,-117.80',
    region: 'Desert + off-road rentals',
    points: '240,85 470,55 505,150 455,255 430,150 265,160',
    labelX: 375,
    labelY: 126,
    summary:
      'San Bernardino County is best for renters looking for UTVs, desert riding trips, off-road weekends, and inland adventure rentals.',
    knownFor:
      'Known for high-desert riding, Johnson Valley-style trips, off-road weekends, and UTV-focused rentals.',
    areas: ['Johnson Valley', 'High desert trips', 'Big Bear area', 'Off-road weekends'],
    popular: [
      { label: 'UTVs', keyword: 'utv' },
      { label: 'Boats', keyword: 'boat' },
      { label: 'Jet Skis', keyword: 'jet ski' },
    ],
  },
  {
    id: 'san-diego-county',
    name: 'San Diego County',
    mapLabel: 'SD',
    bounds: '33.52,-116.08,32.53,-117.60',
    region: 'Coastal + desert rentals',
    points: '280,315 405,335 430,430 360,510 250,485 220,390',
    labelX: 330,
    labelY: 420,
    summary:
      'San Diego County is best for renters looking for coastal water rentals, Mission Bay-style trips, and UTV rentals heading toward desert riding areas.',
    knownFor:
      'Known for a strong mix of coastal jet ski and boat rentals plus desert-access UTV trips.',
    areas: ['Mission Bay area', 'Coastal trips', 'Ocotillo Wells access', 'Weekend adventure rentals'],
    popular: [
      { label: 'Jet Skis', keyword: 'jet ski' },
      { label: 'Boats', keyword: 'boat' },
      { label: 'UTVs', keyword: 'utv' },
    ],
  },
  {
    id: 'imperial-county',
    name: 'Imperial County',
    mapLabel: 'IMP',
    bounds: '33.44,-114.72,32.53,-116.12',
    region: 'Dunes + desert rentals',
    points: '405,335 500,355 510,500 430,570 360,510 430,430',
    labelX: 452,
    labelY: 462,
    summary:
      'Imperial County is best for renters planning dune trips, desert camps, Glamis-style weekends, and UTV-focused off-road rentals.',
    knownFor:
      'Known for dunes, desert camps, off-road weekends, and side-by-side rental demand.',
    areas: ['Imperial Sand Dunes', 'Glamis area', 'Superstition Mountain', 'Heber Dunes'],
    popular: [
      { label: 'UTVs', keyword: 'utv' },
      { label: 'Side-by-sides', keyword: 'side by side' },
    ],
  },
];

const countySearchHref = (county, keyword) => {
  const countyAddress = encodeURIComponent(`${county.name}, CA`);
  const bounds = county.bounds ? `&bounds=${encodeURIComponent(county.bounds)}` : '';
  const query = keyword ? `&keywords=${encodeURIComponent(keyword)}` : '';
  return `/s?address=${countyAddress}${bounds}${query}`;
};

const SoCalCountyExplorer = () => {
  const [selectedCountyId, setSelectedCountyId] = useState(socalCountyRentals[0].id);
  const selectedCounty =
    socalCountyRentals.find(county => county.id === selectedCountyId) || socalCountyRentals[0];

  const selectCounty = countyId => {
    setSelectedCountyId(countyId);
  };

  return (
    <section className={css.countyExplorer}>
      <div className={css.countyExplorerIntro}>
        <p className={css.countyExplorerEyebrow}>Southern California</p>
        <h2>Search rentals by county</h2>
        <p>
          Choose a county from the map or the county cards below. Each county highlights
          the rental types that usually fit that area best, including boats, jet skis,
          and UTVs.
        </p>
      </div>

      <div className={css.countyExplorerLayout}>
        <div className={css.countyMapCard}>
          <div className={css.countyMapHeader}>
            <p>County map</p>
            <span>Click a county</span>
          </div>

          <svg
            className={css.countyMap}
            viewBox="0 0 560 620"
            role="img"
            aria-label="Southern California county rental map"
          >
            <rect x="20" y="20" width="520" height="560" rx="34" className={css.countyMapBg} />

            {socalCountyRentals.map(county => {
              const isSelected = selectedCounty.id === county.id;

              return (
                <g
                  key={county.id}
                  role="button"
                  tabIndex="0"
                  className={css.countyMapLink}
                  onClick={() => selectCounty(county.id)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectCounty(county.id);
                    }
                  }}
                >
                  <polygon
                    points={county.points}
                    className={`${css.countyShape} ${isSelected ? css.countyShapeSelected : ''}`}
                  />
                  <text
                    x={county.labelX}
                    y={county.labelY}
                    className={`${css.countyMapText} ${
                      isSelected ? css.countyMapTextSelected : ''
                    }`}
                  >
                    {county.mapLabel}
                  </text>
                </g>
              );
            })}
          </svg>

          <p className={css.countyMapNote}>
            This is a simplified county selection map for browsing rentals, not a legal
            boundary map.
          </p>
        </div>

        <aside className={css.countySpotlightCard}>
          <p className={css.countyExplorerEyebrow}>Selected county</p>
          <h2>{selectedCounty.name}</h2>
          <span className={css.countySpotlightRegion}>{selectedCounty.region}</span>
          <p>{selectedCounty.summary}</p>

          <div className={css.countyKnownFor}>
            <span>Known for</span>
            <strong>{selectedCounty.knownFor}</strong>
          </div>

          <div className={css.countyPopularGroup}>
            <span className={css.countyLabel}>Popular to rent here</span>
            <div className={css.countyPillRow}>
              {selectedCounty.popular.map(item => (
                <a key={item.label} href={countySearchHref(selectedCounty, item.keyword)}>
                  {item.label}
                </a>
              ))}
            </div>
          </div>

          <div className={css.countyAreaGroup}>
            <span className={css.countyLabel}>Common trip areas</span>
            <ul>
              {selectedCounty.areas.map(area => (
                <li key={area}>{area}</li>
              ))}
            </ul>
          </div>

          <a className={css.countyPrimaryButton} href={countySearchHref(selectedCounty)}>
            Search {selectedCounty.name}
          </a>
        </aside>
      </div>

      <div className={css.countyQuickPanel}>
        <p className={css.countyExplorerEyebrow}>Quick guide</p>
        <h3>Most common rental fit by area</h3>

        <div className={css.countyQuickList}>
          <div>
            <strong>Coastal counties</strong>
            <span>Boats and jet skis</span>
          </div>
          <div>
            <strong>Lake / inland counties</strong>
            <span>Boats, jet skis, and UTVs</span>
          </div>
          <div>
            <strong>Desert counties</strong>
            <span>UTVs and side-by-sides</span>
          </div>
        </div>

        <a className={css.countyPrimaryButton} href="/s">
          Browse all rentals
        </a>
      </div>

      <div className={css.countyCardGrid}>
        {socalCountyRentals.map(county => {
          const isSelected = selectedCounty.id === county.id;

          return (
            <article
              className={`${css.countyRentalCard} ${isSelected ? css.countyRentalCardSelected : ''}`}
              id={county.id}
              key={county.id}
            >
              <div className={css.countyCardTopline}>
                <span>{county.region}</span>
                <a className={css.countySelectLink} href={countySearchHref(county)}>
                  View local listings
                </a>
              </div>

              <h2>{county.name}</h2>
              <p>{county.summary}</p>

              <div className={css.countyPopularGroup}>
                <span className={css.countyLabel}>Popular to rent</span>
                <div className={css.countyPillRow}>
                  {county.popular.map(item => (
                    <a key={item.label} href={countySearchHref(county, item.keyword)}>
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className={css.countyAreaGroup}>
                <span className={css.countyLabel}>Common trip areas</span>
                <ul>
                  {county.areas.map(area => (
                    <li key={area}>{area}</li>
                  ))}
                </ul>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
















const ThrottleXPhase1DepositPolicyNotice = props => {
  const { pageId } = props;
  const showNotice = pageId === 'terms' || pageId === 'faq';

  if (!showNotice) {
    return null;
  }

  return (
    <section className={css.phase1DepositPolicyNotice}>
      <p className={css.phase1DepositEyebrow}>Security deposits</p>
      <h2>Refundable deposit visibility</h2>
      <p>
        Some rentals may require a refundable security deposit. The deposit amount is set by the
        owner and shown before booking when the owner has added one to the listing.
      </p>
      <p>
        For the current launch phase, deposit handling may be confirmed before pickup. The deposit
        may be held or collected before the rental starts and released after the rental is returned
        without damage, missing equipment, late-return issues, fines, or other claimable charges.
      </p>
      <p>
        Automated Stripe deposit holds are planned as a future platform feature. Until then, renters
        should review the listing details and messages carefully before pickup.
      </p>
    </section>
  );
};


const THROTTLEX_FAQ_ITEMS = [
  {
    question: "Is listing free?",
    answer:
      "Yes. Owners can create a listing for free. ThrottleX only earns a commission from completed rentals, which means the platform makes money when owners make money.",
  },
  {
    question: "How does the owner commission work?",
    answer:
      "ThrottleX deducts a platform commission from completed bookings. Owners should set their rental price with the platform commission in mind.",
  },
  {
    question: "How does the security deposit work?",
    answer:
      "Owners can set a refundable security deposit amount for each rental. Renters will see the required deposit amount before booking. The deposit helps protect owners against damage, missing equipment, late returns, or other verified rental issues.",
  },
  {
    question: "Can owners set their own deposit amount?",
    answer:
      "Yes. Each owner can set the deposit amount that makes sense for the rental. Higher-value boats, jet skis, UTVs, or rentals with more risk may require a higher deposit.",
  },
  {
    question: "How does payment work?",
    answer:
      "ThrottleX handles payments associated with the booking. Renters pay through the platform, and owners receive payouts after the rental is completed according to the marketplace payment flow.",
  },
  {
    question: "When do owners get paid?",
    answer:
      "Owner payouts are sent after the rental is completed and the transaction is eligible for payout. Exact payout timing may depend on Stripe, bank processing times, and the status of the transaction.",
  },
  {
    question: "Does ThrottleX screen renters?",
    answer:
      "ThrottleX may use account information, platform activity, payment verification, and other available signals to help support safer transactions. Owners should still complete pickup checks, verify the renter, and document the condition of the rental before handoff.",
  },
  {
    question: "How does insurance work? Do renters need their own?",
    answer:
      "Insurance requirements can vary by rental type, owner, location, and activity. Owners and renters are responsible for understanding what coverage applies before a rental begins. If a rental requires proof of insurance, license, certification, or operator eligibility, that should be clearly listed before booking.",
  },
  {
    question: "When is the pickup address shared?",
    answer:
      "For privacy and security, exact pickup details should only be shared after the booking is confirmed. Renters can review the general area before booking, but private handoff details should stay protected until the transaction is approved.",
  },
  {
    question: "What happens if there is damage?",
    answer:
      "Owners should document the rental condition before and after each trip with clear photos, videos, and notes. Damage claims should be submitted quickly with supporting evidence so the issue can be reviewed.",
  },
  {
    question: "Can owners cancel a booking?",
    answer:
      "Owners should avoid unnecessary cancellations. If a rental becomes unavailable because of safety, maintenance, weather, or another serious issue, the owner should communicate as early as possible.",
  },
  {
    question: "What types of rentals are supported?",
    answer:
      "ThrottleX is built for adventure rentals including jet skis, boats, UTVs, and related outdoor rental experiences.",
  },
];

const ThrottleXFAQContent = () => (
  <div className={css.faqContentGrid}>
    {THROTTLEX_FAQ_ITEMS.map((item, index) => (
      <section className={css.faqContentCard} key={item.question}>
        <div className={css.faqNumber}>{String(index + 1).padStart(2, "0")}</div>
        <div className={css.faqText}>
          <h2>{item.question}</h2>
          <p>{item.answer}</p>
        </div>
      </section>
    ))}
  </div>
);


const policyContent = {
  faq: {
    eyebrow: "Help center",
    title: "Frequently asked questions",
    intro:
      "Answers for renters and owners using ThrottleX for jet ski, boat, and UTV rentals.",
    sections: [],
  },
  terms: {
    eyebrow: 'Legal',
    title: 'Terms of Service',
    intro:
      'These Terms of Service govern access to and use of the ThrottleX Share platform, including the website, communications, booking tools, payment tools, listings, and related services. By creating an account, listing a vehicle, booking a rental, submitting payment, or using the platform, you agree to these Terms.',
    sections: [
      {
        title: 'Effective date',
        body:
          'Effective date: September 14, 2026. These Terms have been reviewed by a qualified attorney.',
      },
      {
        title: 'Our marketplace',
        body:
          'ThrottleX Share is a peer-to-peer online marketplace that connects recreational vehicle owners with renters seeking short-term rentals. The platform currently supports boats, personal watercraft including jet skis, and utility terrain vehicles including UTVs. ThrottleX Share facilitates listings, reservations, payments, communication tools, identity verification, and related platform services. Unless specifically identified otherwise, ThrottleX Share does not own the listed vehicles and is not a rental agency, vehicle dealer, transportation carrier, insurer, financing company, employer, or operator of the owner or renter.',
      },
      {
        title: 'Rental relationship',
        body:
          'Each rental transaction is a direct agreement between the owner and the renter. ThrottleX Share acts as the technology platform that facilitates the transaction. A booking request does not create a rental agreement until the owner accepts the request and the booking is confirmed through the platform.',
      },
      {
        title: 'Eligibility',
        body:
          'To use the platform, users must be at least 18 years old, have legal authority to enter binding contracts, comply with applicable law, and possess any license, permit, boating certificate, driver credential, or other authorization required for the vehicle and rental location. ThrottleX Share or individual owners may require a higher minimum age, such as 21 or 25, depending on vehicle type, law, insurance requirements, or owner preferences.',
      },
      {
        title: 'Account registration',
        body:
          'Users must create an account before booking or listing a vehicle. Users agree to provide accurate, current, and complete information, keep account information updated, safeguard passwords and credentials, and remain responsible for all activity under their account. Multiple accounts created to avoid suspensions, fees, ratings, reviews, or enforcement actions are prohibited.',
      },
      {
        title: 'Identity verification',
        body:
          'ThrottleX Share may require identity verification before allowing a user to book or list vehicles. Verification may include government-issued identification, driver license validation, selfie or facial verification, address verification, email verification, phone verification, payment verification, insurance verification, boating certification verification, motor vehicle records, background screening where permitted by law, and other fraud prevention measures. Verification does not guarantee a user’s identity, skill, honesty, driving ability, boating ability, or future conduct.',
      },
      {
        title: 'Owner responsibilities',
        body:
          'Owners are responsible for owning or having legal authority to rent each listed vehicle, maintaining valid registration where required, maintaining legally required insurance, disclosing known defects, keeping vehicles in safe operating condition, performing recommended maintenance, representing vehicle specifications accurately, disclosing operational limits, providing legally required safety equipment, delivering vehicles in clean condition, and removing hazardous defects before each rental. Owners are solely responsible for legal compliance related to their listings.',
      },
      {
        title: 'Vehicles that may not be listed',
        body:
          'Owners may not knowingly list stolen vehicles, unsafe vehicles, mechanically defective vehicles, vehicles with unresolved safety recalls, vehicles with fraudulent titles, vehicles prohibited by law, or vehicles the owner does not have authority to rent. ThrottleX Share may remove any listing at any time for safety, legal, fraud, quality, or platform reasons.',
      },
      {
        title: 'Renter responsibilities',
        body:
          'Renters agree to operate vehicles responsibly, comply with traffic laws, boating laws, off-highway vehicle regulations, trail restrictions, waterway rules, owner instructions, passenger limits, approved operating areas, safety requirements, and all applicable laws. Renters must wear legally required safety equipment, use life jackets when required, never operate under the influence of alcohol or drugs, never permit unauthorized operators, and return the vehicle in substantially the same condition received.',
      },
      {
        title: 'Prohibited uses',
        body:
          'Unless expressly authorized by the owner and permitted by law, renters may not use vehicles for racing, timed competitions, speed contests, endurance events, demolition activities, rock crawling competitions, mud bog competitions, stunt driving, jumping, intentional drifting, burnouts, wheelies, unauthorized towing, commercial filming, rideshare services, criminal activity, transporting illegal substances, operation while fatigued or distracted, operation under the influence, or operation by unauthorized drivers. Violations may immediately terminate the rental and may void any applicable protection benefits.',
      },
      {
        title: 'UTV and off-road rules',
        body:
          'For UTV and off-road rentals, renters agree to wear seatbelts whenever equipped, wear DOT-approved helmets when required by law or by the owner, obey park and trail rules, remain on legal trails, avoid deep water crossings, avoid intentionally exceeding vehicle operating limits, not disable speed limiters, not modify the vehicle, and not remove, disconnect, shield, or tamper with GPS or tracking devices. Renters remain responsible for rollover damage, collision damage, suspension damage, tire damage caused by misuse, belt damage caused by improper driving, water ingestion, engine over-revving, transmission abuse, recovery expenses, towing charges, and rescue costs caused by misuse, negligence, or operation outside approved areas.',
      },
      {
        title: 'Boat and jet ski rules',
        body:
          'Renters operating boats or personal watercraft must comply with all applicable federal, state, and local boating laws, waterway rules, navigation rules, safety certification requirements, and operator permit requirements. Required safety equipment may include U.S. Coast Guard-approved life jackets, emergency signaling devices, fire extinguishers, navigation lights, and engine cut-off lanyards where equipped or required.',
      },
      {
        title: 'Prohibited watercraft activities',
        body:
          'Unless expressly authorized by the owner and permitted by law, renters may not operate watercraft under the influence, exceed passenger capacity, tow inflatables or riders without owner permission, operate in restricted or no-wake zones in violation of posted rules, intentionally beach a jet ski or boat where prohibited, operate in hazardous weather conditions, race, stunt, operate recklessly, or operate after legal sunset unless the vessel is properly equipped and lawful for night operation.',
      },
      {
        title: 'Watercraft damage responsibility',
        body:
          'Renters are responsible for watercraft damage caused by propeller strikes, grounding, collisions with docks, rocks, or other vessels, improper anchoring, beaching damage, shallow-water operation, trailer damage during renter transport, and fuel contamination caused by improper fueling.',
      },
      {
        title: 'Booking confirmation',
        body:
          'A booking is confirmed only after the booking request is accepted by the owner and processed through the platform. ThrottleX Share may cancel, refuse, or restrict any booking before confirmation for fraud, risk, safety, payment, legal, or operational reasons.',
      },
      {
        title: 'Pickup and return inspections',
        body:
          'At pickup and return, the parties should complete a written or digital inspection documenting vehicle condition, mileage or engine hours, fuel level, existing damage, accessories included, and safety equipment provided. Photos and videos uploaded to the platform may be used as evidence in damage, condition, or return disputes.',
      },
      {
        title: 'Return requirements',
        body:
          'Vehicles must be returned on time, to the agreed location, with the agreed fuel level, with all included accessories, and in substantially the same condition received, excluding ordinary wear specifically permitted by the booking. Late returns may result in additional charges, overage fees, missed-booking losses, towing or recovery charges, and other amounts permitted by these Terms.',
      },
      {
        title: 'Payments, fees, and taxes',
        body:
          'Renters authorize ThrottleX Share and its payment processors to charge all amounts associated with a booking, including rental fees, platform service fees, insurance or protection fees where offered, taxes, security deposits, fuel charges, cleaning fees, late return fees, damage charges, towing or recovery charges, and dispute-related charges authorized under these Terms. Owners are responsible for determining and paying taxes applicable to their rental income unless ThrottleX Share is legally required to collect or remit taxes.',
      },
      {
        title: 'Marketplace service fees',
        body:
          'ThrottleX Share currently charges an owner marketplace service fee equal to 10% of the booking amount and a renter marketplace service fee equal to 10% of the booking amount. Service fees are separate from applicable taxes, processing fees, security deposits, optional charges, delivery fees, fuel fees, cleaning fees, and damage charges. ThrottleX Share may modify marketplace service fees with reasonable advance notice through the platform.',
      },
      {
        title: 'Payment processing',
        body:
          'ThrottleX Share uses Stripe or another approved third-party payment processor to facilitate transactions. Users authorize ThrottleX Share to collect rental payments, collect marketplace service fees, collect taxes where required, place authorization holds for security deposits, issue refunds, process chargebacks, and remit owner payouts after applicable deductions. ThrottleX Share does not store complete payment card information.',
      },
      {
        title: 'Owner payouts',
        body:
          'Owners authorize ThrottleX Share to collect rental payments on their behalf and remit payouts according to the platform’s payout schedule after deducting applicable marketplace service fees, authorized refunds, chargebacks, taxes required to be withheld, damage claims, disputes, and other permitted deductions. Payouts may be delayed during fraud reviews, payment disputes, chargebacks, investigations, unresolved claims, or legal compliance reviews.',
      },
      {
        title: 'Security deposits',
        body:
          'Each owner determines the security deposit required for a listing. Security deposits may vary based on vehicle type, vehicle value, rental duration, anticipated risk, and owner preference. ThrottleX Share may place a temporary authorization hold or collect the required security deposit before the rental begins. A security deposit does not limit the renter’s liability for losses exceeding the deposit amount.',
      },
      {
        title: 'Damage reporting and claims',
        body:
          'Owners should report alleged damage as soon as reasonably possible after the rental ends and no later than 72 hours after the scheduled conclusion of the rental period unless a longer period is required by law or platform policy. Damage reports should include available photographs, videos, repair estimates, invoices, diagnostic reports, receipts, written descriptions, and supporting documentation requested by ThrottleX Share.',
      },
      {
        title: 'Damage claim review',
        body:
          'ThrottleX Share may review evidence submitted by both parties to help facilitate resolution of a damage dispute. Users acknowledge that ThrottleX Share is not acting as an insurance adjuster, arbitrator, judge, or independent mechanical expert. ThrottleX Share may request additional documentation before making any platform-related determination.',
      },
      {
        title: 'Charge authorization for damage',
        body:
          'If a renter is determined to be financially responsible for damage, the renter authorizes ThrottleX Share and its payment processors to charge the payment method on file for repair costs, replacement costs, towing and recovery expenses, storage fees, cleaning fees, administrative processing fees, diminished value where permitted by law, lost equipment, and other amounts authorized under these Terms.',
      },
      {
        title: 'Cancellations and refunds',
        body:
          'Owners are expected to honor confirmed bookings. Repeated cancellations, excessive unavailability, or failure to deliver the listed vehicle may result in cancellation fees, reduced search visibility, temporary suspension, permanent removal, or other enforcement action. Renter refund eligibility depends on the cancellation policy selected for the booking, the timing of cancellation, applicable law, platform policy, and any approved extenuating circumstances. Platform service fees may be non-refundable unless otherwise stated.',
      },
      {
        title: 'Weather and mechanical issues',
        body:
          'Weather alone does not automatically entitle a renter to a refund unless required by law, provided under the applicable cancellation policy, or approved by ThrottleX Share. Owners and renters are encouraged to communicate about weather concerns before the rental begins. If a listed vehicle becomes unavailable before the rental begins due to verified mechanical failure, ThrottleX Share may provide a full or partial refund, issue platform credit, assist in locating an alternative vehicle, or take other reasonable action.',
      },
      {
        title: 'GPS tracking and telematics',
        body:
          'Many listed vehicles may contain GPS tracking devices, telematics systems, engine monitoring systems, geofencing technology, or other location-based equipment. By renting or operating a vehicle through the platform, renters consent to the collection and use of operational and location data during the rental period for theft prevention, vehicle recovery, emergency assistance, mileage or engine-hour verification, speed and operational monitoring where permitted, insurance administration, dispute resolution, and enforcement of these Terms.',
      },
      {
        title: 'GPS tampering prohibited',
        body:
          'Renters may not disable, disconnect, shield, remove, modify, or interfere with any GPS, telematics, engine monitoring, or anti-theft equipment installed in a vehicle. Tampering with such equipment is a material breach of these Terms and may result in immediate termination of the rental, forfeiture of applicable protection benefits, additional charges, and permanent suspension from the platform.',
      },
      {
        title: 'Rental privacy',
        body:
          'ThrottleX Share will handle personal information and telematics data in accordance with its Privacy Policy and applicable law. Operational data collected during a rental may be shared with the vehicle owner, insurers, claims administrators, law enforcement, or other authorized parties when reasonably necessary to investigate accidents, theft, fraud, legal claims, or violations of these Terms.',
      },
      {
        title: 'Insurance disclosure',
        body:
          'ThrottleX Share is not an insurance company and does not provide insurance coverage unless expressly identified in a separate written protection plan or insurance agreement. ThrottleX Share does not currently provide a host protection program. Owners are solely responsible for maintaining insurance required by applicable law and determining whether they maintain appropriate coverage for peer-to-peer rentals. Renters acknowledge that they may be personally responsible for damage, injury, property loss, and third-party claims arising from their use of a rented vehicle.',
      },
      {
        title: 'Protection plans and risk allocation',
        body:
          'Any insurance or protection products offered through the platform are subject to their own terms, conditions, exclusions, deductibles, coverage limits, and eligibility requirements. ThrottleX Share does not guarantee that any insurance policy or protection plan will apply to a particular incident. Coverage determinations are made solely by the applicable insurer or protection provider.',
      },
      {
        title: 'Assumption of risk',
        body:
          'Users acknowledge that recreational activities involving boats, personal watercraft, UTVs, trailers, and similar vehicles involve inherent risks that cannot be eliminated. These risks may include collisions, rollovers, capsizing, drowning, changing weather conditions, equipment failure, mechanical defects, terrain hazards, wildlife encounters, submerged objects, falling, fire, explosion, serious bodily injury, permanent disability, and death. By participating in these activities, each user voluntarily assumes all known and unknown risks associated with the rental and operation of recreational vehicles to the fullest extent permitted by law.',
      },
      {
        title: 'Release of liability',
        body:
          'To the fullest extent permitted by law, each user releases and discharges ThrottleX Share, its affiliates, officers, directors, employees, contractors, licensors, agents, successors, and assigns from claims arising out of or related to vehicle operation, accidents, personal injuries, death, property damage, theft, weather conditions, mechanical failures, owner conduct, renter conduct, third-party conduct, acts of nature, delayed reservations, interrupted trips, and loss of personal property, except where prohibited by applicable law.',
      },
      {
        title: 'Indemnification',
        body:
          'Users agree to defend, indemnify, and hold harmless ThrottleX Share and its affiliates from any claim, demand, lawsuit, investigation, fine, penalty, liability, judgment, settlement, expense, or attorney’s fee arising from violation of these Terms, misuse of the platform, negligent operation of a vehicle, intentional misconduct, violation of applicable law, intellectual property infringement, bodily injury, death, property damage, or disputes between owners and renters.',
      },
      {
        title: 'Limitation of liability',
        body:
          'To the maximum extent permitted by law, ThrottleX Share shall not be liable for personal injuries, death, vehicle damage, lost profits, business interruption, emotional distress, reputational harm, lost opportunities, indirect damages, consequential damages, incidental damages, punitive damages, or exemplary damages. If ThrottleX Share is found liable despite these limitations, its aggregate liability shall not exceed the greater of the total service fees paid to ThrottleX Share for the applicable booking or $250, unless a different limitation is required by law.',
      },
      {
        title: 'Platform conduct',
        body:
          'Users may not provide false information, impersonate another person, use fraudulent payment methods, circumvent platform fees, complete rentals outside the platform after introduction through the platform, manipulate reviews or ratings, threaten or harass other users, upload malicious software, interfere with platform security, access another user account without authorization, engage in money laundering or fraudulent transactions, or use the platform for illegal purposes.',
      },
      {
        title: 'Suspension and termination',
        body:
          'ThrottleX Share may suspend, restrict, or terminate accounts at its discretion for fraud, repeated cancellations, unsafe conduct, excessive damage claims, payment failures, violations of these Terms, criminal activity, abuse of customer support, legal or regulatory requirements, or other platform-protection reasons. Termination does not eliminate outstanding financial obligations owed to or by a user.',
      },
      {
        title: 'Intellectual property',
        body:
          'The ThrottleX Share platform, including software, website design, source code, databases, graphics, logos, trademarks, service marks, trade dress, text, videos, photographs, audio, compilations, and other content, is owned by or licensed to ThrottleX Share and protected by intellectual property laws. Users may not copy, reproduce, modify, distribute, sell, sublicense, reverse engineer, decompile, scrape, harvest data from, or create derivative works from the platform except as permitted by these Terms.',
      },
      {
        title: 'User content and reviews',
        body:
          'Users retain ownership of photos, descriptions, reviews, videos, messages, and other content they upload. By submitting content, users grant ThrottleX Share a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host, reproduce, publish, display, distribute, format, advertise, and promote that content for platform purposes. Reviews must reflect genuine user experiences and may not be purchased, manipulated, threatened, or knowingly false.',
      },
      {
        title: 'Electronic communications and signatures',
        body:
          'By creating an account or using the platform, users consent to receive communications electronically, including booking confirmations, receipts, invoices, legal notices, policy updates, account notifications, and marketing communications subject to applicable opt-out rights. Clicking buttons such as Accept, Agree, Book Now, Confirm Reservation, or similar actions may constitute an electronic signature with the same legal effect as a handwritten signature to the fullest extent permitted by law.',
      },
      {
        title: 'Privacy',
        body:
          'ThrottleX Share collects, uses, stores, and shares personal information as described in its Privacy Policy. By using the platform, users consent to the collection and processing of information reasonably necessary to provide platform services, process payments, verify identity, detect fraud, investigate claims, comply with legal obligations, and improve platform functionality.',
      },
      {
        title: 'Copyright complaints',
        body:
          'ThrottleX Share respects intellectual property rights. If a user believes content on the platform infringes a copyright, the user may submit a written notice identifying the copyrighted work, the allegedly infringing material, contact information, a good-faith statement, a statement made under penalty of perjury that the information is accurate and authorized, and a physical or electronic signature. ThrottleX Share may remove allegedly infringing material and terminate repeat infringers where appropriate.',
      },
      {
        title: 'Dispute resolution and arbitration',
        body:
          'Except where prohibited by law, any dispute, claim, or controversy arising out of or relating to these Terms, the platform, or any booking shall be resolved through final and binding arbitration rather than in court. Arbitration shall be administered by a nationally recognized arbitration provider selected by ThrottleX Share, conducted by a single neutral arbitrator, and may take place remotely or in the applicable venue permitted by the arbitration rules.',
      },
      {
        title: 'Class action waiver',
        body:
          'To the fullest extent permitted by law, users agree that disputes shall be brought only in an individual capacity. Users waive the right to participate in a class action, serve as a class representative, join a consolidated proceeding, or participate in a private attorney general action except where such waiver is prohibited by law.',
      },
      {
        title: 'Governing law and venue',
        body:
          'These Terms shall be governed by the laws of the state designated in ThrottleX Share’s corporate formation documents, without regard to conflict-of-law principles. Unless arbitration is required, any legal action shall be brought in the state or federal courts having jurisdiction over ThrottleX Share’s principal place of business, and each user consents to the personal jurisdiction of those courts.',
      },
      {
        title: 'Changes to these Terms',
        body:
          'ThrottleX Share may update these Terms from time to time. Material changes may be posted through the platform or provided by other reasonable notice. Continued use of the platform after the effective date of updated Terms constitutes acceptance of the revised Terms.',
      },
      {
        title: 'Definitions',
        body:
          'Platform means the ThrottleX Share website, mobile applications, software, communications, and related services. Owner means a user who lists or makes a vehicle available for rental. Renter means a user who books or operates a listed vehicle. Vehicle means boats, personal watercraft, UTVs, trailers, and other recreational equipment approved by ThrottleX Share. Booking means a confirmed reservation made through the platform. Protection Plan means any optional third-party damage protection or insurance product offered through the platform. User means any person or entity accessing or using the platform.',
      },
      {
        title: 'Contact information',
        body:
          'Questions regarding these Terms, bookings, claims, safety concerns, or use of the platform may be directed to ThrottleX Share LLC at support@throttlexshare.com or through the official customer support channels available on the platform.',
      },
    ],
  },

  'socal-rentals': {
    eyebrow: 'Explore rentals',
    title: 'Southern California Rentals by County',
    intro:
      'Find ThrottleX rentals by Southern California county. This guide helps renters choose the right area for boats, jet skis, and UTVs based on the type of trip they are planning.',
    sections: [],
  },

  privacy: {
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    intro:
      'This Privacy Policy explains how ThrottleX Share collects, uses, stores, protects, and shares personal information when you use the ThrottleX marketplace, create an account, list or rent a vehicle, complete a transaction, or communicate through the platform.',
    sections: [
      {
        title: 'Information collected',
        body:
          'ThrottleX may collect account information, contact details, identity-verification information, booking details, listing information, messages, payment-related information, device information, approximate location information, and website-usage data.',
      },
      {
        title: 'How information may be used',
        body:
          'Information may be used to operate the marketplace, verify users, support bookings, process transactions, communicate with users, improve the platform, prevent fraud, resolve disputes, enforce agreements, and comply with legal obligations.',
      },
      {
        title: 'Payment information',
        body:
          'Payment and payout information may be processed by third-party payment providers. ThrottleX may receive limited transaction details but should not directly store complete payment-card numbers unless expressly required and handled through a compliant provider.',
      },
      {
        title: 'Service providers',
        body:
          'ThrottleX may use third-party providers for marketplace hosting, payments, identity verification, maps, analytics, email, text messaging, fraud prevention, customer service, and other business operations.',
      },
      {
        title: 'Information sharing',
        body:
          'Information may be shared with hosts, renters, service providers, insurers, claims administrators, government authorities, or other parties when reasonably necessary to support a booking, investigate an incident, prevent misuse, or comply with law.',
      },
      {
        title: 'Data choices and rights',
        body:
          'Depending on applicable law, users may have rights to access, correct, delete, restrict, or obtain copies of certain personal information. Privacy-related requests may be submitted to support@throttlexshare.com.',
      },
      {
        title: 'Policy scope and additional notices',
        body:
          'This Privacy Policy applies to information processed through ThrottleX Share and should be read together with the ThrottleX Terms of Service and any additional notices presented when specific information is collected.',
      },
    ],
  },

  'safety-guidelines': {
    eyebrow: 'Safety',
    title: 'Safety Guidelines',
    intro:
      'These safety guidelines describe general expectations for renters, operators, passengers, and hosts using ThrottleX.',
    sections: [
      {
        title: 'Review requirements before booking',
        body:
          'Renters should review age limits, license requirements, safety equipment, experience requirements, pickup instructions, approved operating areas, weather conditions, and prohibited uses before booking.',
      },
      {
        title: 'Use proper safety equipment',
        body:
          'Renters and passengers should use helmets, personal flotation devices, protective clothing, seat belts, eye protection, and other equipment required by the vehicle type, applicable law, operating location, or host instructions.',
      },
      {
        title: 'Inspect before operation',
        body:
          'Before operating a rental, the renter should inspect the vehicle and included equipment, review operating instructions, photograph existing damage, and notify the host immediately if anything appears unsafe, damaged, missing, or inconsistent with the listing.',
      },
      {
        title: 'Know the controls',
        body:
          'Operators should understand the vehicle’s controls, starting and stopping procedures, braking, steering, emergency shutoff systems, loading limits, fueling requirements, and other essential operating instructions before use.',
      },
      {
        title: 'Operate responsibly',
        body:
          'Vehicles should be operated carefully, legally, and only in approved areas. Reckless use, impaired operation, unauthorized operators, overloading, racing, and use outside permitted locations are prohibited.',
      },
      {
        title: 'Monitor weather and conditions',
        body:
          'Users should monitor weather, water, visibility, trail, fire, traffic, and environmental conditions. Operation should be postponed or stopped when conditions become unsafe.',
      },
      {
        title: 'Do not operate unsafe equipment',
        body:
          'A renter should not operate a vehicle when its mechanical condition, available safety equipment, weather, terrain, visibility, operator condition, or passenger behavior creates an unreasonable safety risk.',
      },
      {
        title: 'Emergency preparation',
        body:
          'Renters should know how to contact emergency services, identify their operating location, carry appropriate communication equipment, and follow all emergency instructions provided by the host or local authorities.',
      },
    ],
  },

  'rental-rules': {
    eyebrow: 'Rules',
    title: 'Rental Rules',
    intro:
      'These development-stage rental rules describe general expectations that may apply to bookings made through ThrottleX.',
    sections: [
      {
        title: 'Pickup and return',
        body:
          'Renters must follow the host’s approved pickup, delivery, launch, loading, return, and late-return instructions. Hosts should clearly explain pickup windows, return expectations, meeting locations, and required condition documentation.',
      },
      {
        title: 'Authorized operators',
        body:
          'Only operators approved through the booking and permitted by the host, applicable law, and any insurance or protection requirements may operate a rental.',
      },
      {
        title: 'Passengers and capacity',
        body:
          'Renters must follow seating, weight, passenger, and equipment-capacity limits. Renters must not transport more people or equipment than the vehicle is designed or legally permitted to carry.',
      },
      {
        title: 'Damage and deposits',
        body:
          'Hosts must clearly explain deposits, damage policies, cleaning charges, fuel requirements, mileage or usage limits, late fees, towing or recovery costs, and additional charges before accepting a booking.',
      },
      {
        title: 'Fuel and charging',
        body:
          'Renters must follow the agreed fuel or charging policy and use only the fuel, charging equipment, or energy source approved for the rental.',
      },
      {
        title: 'Prohibited use',
        body:
          'Prohibited activities may include reckless operation, illegal activity, racing, impaired operation, unauthorized towing, overloading, commercial use, subleasing, unauthorized operators, and use outside approved waterways, roads, trails, or geographic areas.',
      },
      {
        title: 'Return condition',
        body:
          'Renters must return the rental at the agreed time, location, fuel level, cleanliness level, and condition, subject to reasonable wear specifically permitted by the applicable rental agreement.',
      },
      {
        title: 'Mechanical problems',
        body:
          'Renters should stop operating the rental and contact the host when warning lights, unusual sounds, leaks, handling problems, damage, overheating, or other mechanical concerns appear.',
      },
    ],
  },

  insurance: {
    eyebrow: 'Coverage',
    title: 'Insurance and Protection',
    intro:
      'ThrottleX has not yet finalized its insurance or protection program. No insurance, damage waiver, or protection should be assumed unless it is expressly identified during booking and supported by separate written terms.',
    sections: [
      {
        title: 'Coverage is not automatic',
        body:
          'Creating an account, publishing a listing, or booking through ThrottleX does not by itself guarantee insurance coverage. Coverage applies only when expressly stated in the booking flow or an applicable policy, certificate, protection agreement, or endorsement.',
      },
      {
        title: 'Coverage may be limited',
        body:
          'Any future insurance, protection plan, damage waiver, or reimbursement program may include eligibility requirements, deductibles, limits, exclusions, reporting deadlines, geographic restrictions, approved-use requirements, and other conditions.',
      },
      {
        title: 'Personal policies may not apply',
        body:
          'Renters and hosts should not assume that a personal auto, marine, recreational-vehicle, homeowners, or umbrella policy covers peer-to-peer rental activity. Each user is responsible for confirming coverage directly with the applicable insurer.',
      },
      {
        title: 'Host responsibility',
        body:
          'Hosts are responsible for maintaining any insurance required by law, contract, lender, marina, storage facility, property owner, or other applicable party and for confirming whether rental or commercial activity is permitted.',
      },
      {
        title: 'Renter responsibility',
        body:
          'Renters remain responsible for costs not covered by an applicable insurance policy or protection product, including deductibles, exclusions, prohibited-use losses, and other amounts assigned under the booking agreement.',
      },
      {
        title: 'Excluded or prohibited activity',
        body:
          'Coverage may not apply when a vehicle is operated by an unauthorized person, outside an approved area, while impaired, during racing or commercial use, or in violation of a policy, protection agreement, booking rule, or law.',
      },
      {
        title: 'Claims and documentation',
        body:
          'Users may be required to report incidents promptly, provide photographs, statements, police or incident reports, maintenance records, repair estimates, and other documentation requested by an insurer or claims administrator.',
      },
      {
        title: 'Final insurance review required',
        body:
          'All insurance, waiver, protection, claims, deductible, and reimbursement language must be reviewed by qualified insurance professionals and attorneys before ThrottleX begins accepting live reservations.',
      },
    ],
  },

  liability: {
    eyebrow: 'Risk and Liability',
    title: 'Risk, Liability and Insurance Disclosure',
    intro:
      'Motorized watercraft, boats, UTVs, trailers, towing, launching, loading, off-road driving, and related outdoor activities involve significant risks. This disclosure summarizes important risk, responsibility, insurance, and liability considerations that apply to ThrottleX Share users and should be read together with the Terms of Service, booking terms, waivers, insurance or protection documents, and other applicable agreements.',
    sections: [
      {
        title: 'ThrottleX is a marketplace platform',
        body:
          'ThrottleX provides an online marketplace intended to help independent hosts and renters find one another, communicate, and arrange bookings. Unless expressly stated otherwise, ThrottleX does not own, manufacture, maintain, repair, inspect, store, transport, launch, retrieve, or operate vehicles listed by independent hosts.',
      },
      {
        title: 'Hosts and renters act independently',
        body:
          'Hosts are independent vehicle owners or rental operators, and renters independently decide whether a listing, vehicle, host, location, activity, or booking is appropriate for them. A listing appearing on ThrottleX should not be interpreted as a guarantee that the vehicle or activity is safe, lawful, suitable, insured, or appropriate for a particular user.',
      },
      {
        title: 'Acknowledgment of inherent risks',
        body:
          'The renter, operator, and each participant acknowledge that recreational vehicles and outdoor activities can involve foreseeable and unforeseeable hazards, including collisions, rollovers, falls, drowning, capsizing, fire, mechanical failure, equipment failure, towing incidents, loading or launching injuries, wildlife, terrain hazards, changing weather, poor visibility, water conditions, trail conditions, and the acts of other people.',
      },
      {
        title: 'Risk of serious injury or death',
        body:
          'These hazards may result in property damage, financial loss, bodily injury, permanent disability, illness, or death. Each user is responsible for evaluating the risks, exercising reasonable judgment, and deciding whether to proceed with a booking or activity.',
      },
      {
        title: 'Operator eligibility and ability',
        body:
          'A renter or operator must have the age, physical ability, experience, judgment, licensing, permits, certifications, and legal authority required for the vehicle and operating location. Users must not misrepresent their qualifications or permit an unauthorized person to operate a rental.',
      },
      {
        title: 'Pre-rental inspection and documentation',
        body:
          'Before accepting or operating a rental, the renter should inspect the vehicle, trailer, controls, tires, hull, safety equipment, fluids, fuel level, lights, restraints, personal flotation devices, helmets, and other relevant components. The renter and host should document the condition with clear photographs or video and record any existing damage.',
      },
      {
        title: 'Refusal of unsafe equipment',
        body:
          'A renter should not accept or operate a rental that appears damaged, improperly maintained, incorrectly equipped, inconsistent with the listing, or unsafe. Safety concerns should be reported to the host and ThrottleX before operation whenever reasonably possible.',
      },
      {
        title: 'Renter responsibility during the booking',
        body:
          'The booking renter is responsible for the rental during the reservation period and may be responsible for the conduct of approved operators, passengers, guests, invitees, and other people given access to the vehicle or equipment.',
      },
      {
        title: 'Damage, theft and related costs',
        body:
          'Subject to the applicable booking agreement, protection terms, insurance policy, and law, the renter may be financially responsible for physical damage, theft, loss, vandalism, misuse, excessive wear, missing equipment, cleaning, fuel, towing, recovery, storage, impound charges, diminished value, loss of use, administrative costs, deductibles, and other booking-related losses.',
      },
      {
        title: 'Host responsibilities',
        body:
          'Hosts are responsible for accurately describing the rental, disclosing known defects or restrictions, maintaining the vehicle and equipment, following applicable registration and inspection requirements, providing promised safety equipment, and delivering the rental in the condition represented in the listing and booking agreement.',
      },
      {
        title: 'Insurance and protection limitations',
        body:
          'No insurance, liability coverage, protection plan, damage waiver, or reimbursement is guaranteed unless expressly included in the booking and documented in separate applicable terms. Any such product may contain limits, exclusions, deductibles, eligibility requirements, approved-use rules, and claims procedures.',
      },
      {
        title: 'Weather and operating conditions',
        body:
          'Renters and hosts should cancel, postpone, stop, or refuse an activity when weather, fire risk, water conditions, trail closures, mechanical concerns, visibility, operator condition, passenger behavior, or another circumstance makes the activity unsafe or unlawful.',
      },
      {
        title: 'Alcohol, drugs and reckless conduct',
        body:
          'A rental must never be operated while the operator is impaired by alcohol, cannabis, illegal drugs, prescription medication, fatigue, illness, or any other condition that reduces safe operating ability. Reckless operation, racing, stunts, unauthorized towing, intentional misuse, overloading, and illegal activity are prohibited.',
      },
      {
        title: 'Compliance with laws and local rules',
        body:
          'Users are responsible for following all applicable federal, state, tribal, county, municipal, marine, park, trail, marina, environmental, licensing, registration, equipment, passenger, speed, towing, and operating rules.',
      },
      {
        title: 'Accidents and incident reporting',
        body:
          'After an accident, injury, theft, breakdown, fire, or other incident, users should first seek emergency assistance when necessary. They must then notify the host and ThrottleX as soon as reasonably possible, preserve photographs and records, obtain relevant contact information, avoid unauthorized repairs or admissions, and cooperate with insurers, authorities, claims administrators, and other involved parties.',
      },
      {
        title: 'Release and limitation of liability',
        body:
          'To the fullest extent permitted by applicable law and subject to the final Terms of Service, users may be required to release and limit claims against ThrottleX arising from risks inherent in independently owned rentals, host or renter conduct, third-party services, outdoor conditions, and activities arranged through the marketplace. Nothing in this disclosure excludes responsibility that cannot legally be excluded.',
      },
      {
        title: 'Indemnification',
        body:
          'To the fullest extent permitted by applicable law and subject to the final Terms of Service, users may be required to defend, indemnify, and hold ThrottleX and its affiliates harmless from claims, losses, liabilities, penalties, and expenses arising from their listings, bookings, conduct, legal violations, property damage, injuries, unauthorized use, or breach of an applicable agreement.',
      },
      {
        title: 'Separate agreements may apply',
        body:
          'A host may require a booking-specific rental agreement, safety acknowledgment, inspection form, participant waiver, security deposit, operating-area agreement, or other document. Those documents may create additional obligations and should be reviewed carefully before the rental begins.',
      },
      {
        title: 'Legal and insurance framework',
        body:
          'This disclosure forms part of the risk and liability information presented to ThrottleX Share users. Users should review the Terms of Service, applicable host and renter requirements, waivers, insurance or protection documents, checkout disclosures, claims procedures, and other agreements that apply to a booking. Nothing in this disclosure limits rights or obligations that cannot be limited under applicable law.',
      },
    ],
  },

  contact: {
    eyebrow: 'Support',
    title: 'Contact ThrottleX',
    intro:
      'For support, use the ThrottleX contact form or email support@throttlexshare.com.',
    sections: [
      {
        title: 'General support',
        body:
          'Add your preferred customer-support email address, business phone number, mailing address, and normal support hours through the ThrottleX Share platform.',
      },
      {
        title: 'Booking support',
        body:
          'Renters and hosts should have a clear place to request help with bookings, listing questions, payments, cancellations, refunds, claims, and account issues.',
      },
      {
        title: 'Accidents and claims',
        body:
          'Add clear instructions explaining how renters and hosts should report accidents, injuries, theft, property damage, breakdowns, and other incidents.',
      },
      {
        title: 'Emergency situations',
        body:
          'ThrottleX should not be treated as an emergency-response service. In an immediate emergency, users should contact 911, the Coast Guard, park authorities, local law enforcement, or the appropriate emergency service before contacting marketplace support.',
      },
      {
        title: 'Partnerships and fleet owners',
        body:
          'This section can include contact information for rental operators, fleet owners, insurance partners, marinas, storage facilities, and other businesses interested in partnering with ThrottleX.',
      },
    ],
  },

  'utv-renter-safety-checklist': {
    eyebrow: 'UTV safety',
    downloadHref: '/static/docs/utv-renter-safety-checklist.pdf',
    downloadLabel: 'Download printable UTV checklist',
    title: 'UTV Renter Safety Checklist',
    intro:
      'This checklist is designed for UTV rentals on ThrottleX. Owners and renters should review these items before handoff so the rental starts with clear expectations, verified safety gear, and documented vehicle condition.',
    sections: [
      {
        title: 'Rental documentation',
        body:
          'Before the UTV is released, the owner should verify the renter’s government-issued ID, confirm the booking, and confirm emergency contact information. The rental number and rental date should be documented for the handoff record.',
      },
      {
        title: 'Required safety gear',
        body:
          'Seat belts should be inspected before operation. Helmets should be provided when included with the rental or required by law, location, trail rules, insurance requirements, or owner instructions. Goggles or eye protection and gloves are recommended. Closed-toe shoes are required for UTV operation.',
      },
      {
        title: 'Vehicle inspection',
        body:
          'Before departure, the owner and renter should inspect the tires, lug nuts, suspension, lights, horn, brakes, steering, fuel level, coolant level, and oil level. If the UTV includes a winch, the owner should explain basic operation. If equipped, the fire extinguisher should be present and accessible.',
      },
      {
        title: 'Owner demonstration',
        body:
          'The owner should demonstrate the starting procedure, four-wheel-drive controls, parking brake, reverse gear, low and high range selection, fuel requirements, GPS system if equipped, and trailer loading instructions if the rental involves trailering.',
      },
      {
        title: 'Safe operation rules',
        body:
          'The renter should confirm that they understand the core UTV safety rules: always wear seat belts, keep arms and legs inside the vehicle, stay on approved trails, do not exceed passenger capacity, reduce speed on rough terrain, avoid steep side hills, avoid deep water crossings, do not race, do not jump, do not drift, do not operate under the influence, and do not allow unauthorized drivers.',
      },
      {
        title: 'Emergency procedures',
        body:
          'The owner should review rollover response, fire response, mechanical breakdown procedures, recovery or towing procedures, emergency contact information, and any GPS tracking disclosure that applies to the vehicle.',
      },
      {
        title: 'Return requirements',
        body:
          'Before the rental begins, the owner and renter should review the fuel policy, cleaning expectations, sand or mud removal requirements, damage reporting procedure, and confirmed return location.',
      },
      {
        title: 'Renter acknowledgment',
        body:
          'Before operating the UTV, the renter should acknowledge that they received instruction on safe operation and understand the safety rules, operating limits, and emergency procedures explained before the rental. The handoff record should include renter signature, owner signature, and date where required by the owner or rental process.',
      },
    ],
  },

  'host-standards': {
    eyebrow: 'Owner standards',
    title: 'Host Standards',
    intro:
      'These standards help ThrottleX owners create safer rentals, cleaner handoffs, stronger communication, and a more reliable experience for every renter.',
    sections: [
      {
        title: 'Owner performance standards',
        body:
          'ThrottleX owners are expected to maintain safe vehicles, accurate listings, respectful communication, and reliable booking behavior. These standards support the Terms of Service and Owner Agreement and may be used to review listing quality, account standing, search visibility, and platform eligibility.',
      },
      {
        title: 'Minimum performance targets',
        body:
          'Owners should aim to maintain a 4.5-star or higher average rating, accept at least 85% of valid booking requests when the vehicle is available, respond to new inquiries within 12 hours, and keep owner-initiated cancellations below 2%. Preferred response time is one hour or less whenever possible.',
      },
      {
        title: 'Pickup and return reliability',
        body:
          'Vehicles should be ready at the scheduled pickup or delivery time. Owners should target 98% on-time handoffs and complete return inspections promptly. Damage claims, missing equipment reports, or post-rental issues should be submitted within 72 hours after the scheduled return whenever possible.',
      },
      {
        title: 'Clean and rental-ready vehicles',
        body:
          'Every rental should be clean, fueled or charged as advertised, safe to operate, and ready for the renter. Boats should have clean decks, seating, storage areas, windshields, and functional lights. Jet skis should be rinsed, clean, properly fueled, and free from excessive debris. UTVs should have clean seats, empty cargo areas, no excessive mud buildup, and no loose or unsafe parts.',
      },
      {
        title: 'Maintenance and safety checks',
        body:
          'Owners are responsible for following manufacturer maintenance schedules, repairing known defects, completing safety recalls, checking tires and trailers, and confirming that required safety equipment is available before each booking. Vehicles with known safety defects should not be rented.',
      },
      {
        title: 'Accurate listing quality',
        body:
          'Listings should include current photos, accurate descriptions, seating capacity, engine or horsepower details where helpful, operating instructions, rental restrictions, included accessories, fuel requirements, and pickup or delivery expectations. Photos must represent the vehicle’s current condition and should not mislead renters.',
      },
      {
        title: 'Customer service expectations',
        body:
          'Owners should communicate clearly and respectfully, answer reasonable renter questions, provide accurate pickup instructions, explain vehicle operation, respond to urgent issues, and notify renters quickly if there are unexpected delays or safety concerns.',
      },
      {
        title: 'Safety-first handoffs',
        body:
          'Owners should not release a vehicle to a renter who appears intoxicated, appears under the influence, presents fraudulent identification, refuses required instructions, intends to violate the law, or creates a serious safety concern. Serious safety issues should be reported to ThrottleX.',
      },
      {
        title: 'Prohibited owner conduct',
        body:
          'Owners may not knowingly rent unsafe vehicles, falsify maintenance records, manipulate reviews, request off-platform payments for ThrottleX bookings, discriminate unlawfully, retaliate against honest reviews, submit fraudulent damage claims, misrepresent vehicle condition, inflate repair estimates, or tamper with GPS or telematics records.',
      },
      {
        title: 'High-performing owners',
        body:
          'ThrottleX may recognize strong owners through Top Host or similar programs. Eligibility may consider a 4.9-star or higher average rating, response times under one hour, cancellation rates below 1%, at least 10 completed rentals in the previous 12 months, and continued compliance with safety and maintenance standards.',
      },
      {
        title: 'Performance reviews and corrective action',
        body:
          'ThrottleX may review owner performance using ratings, response times, cancellations, completed bookings, renter complaints, safety reports, claim history, fraud indicators, and policy compliance. Depending on the issue, corrective action may include coaching, written warnings, listing updates, reduced visibility, additional verification, temporary suspension, or account removal.',
      },
      {
        title: 'Continuous improvement',
        body:
          'ThrottleX may update these standards over time to improve safety, customer satisfaction, operational consistency, and regulatory compliance. Continued use of the platform after updated standards are posted means the updated standards apply.',
      },
    ],
  },
};

const PolicyPage = props => {
  
  /* THROTTLEX_FORCE_CONTACT_FORM_START */
  const forcedPageId =
    props?.params?.pageId ||
    props?.match?.params?.pageId ||
    props?.route?.params?.pageId;

  if (forcedPageId === 'contact') {
    return <ContactPage />;
  }
  /* THROTTLEX_FORCE_CONTACT_FORM_END */

const pageId = props.params?.pageId || 'terms';
  const page = policyContent[pageId] || policyContent.terms;
  const currentYear = new Date().getFullYear();
  const showDevelopmentNotice = pageId !== 'terms';

  return (
    <Page title={`${page.title} | ThrottleX`} description={page.intro}>
      <TopbarContainer currentPage="PolicyPage" />

      <main id="main-content" className={css.page}>
        <section className={css.hero}>
          <div className={css.heroGlowBlue} />
          <div className={css.heroGlowOrange} />

          <div className={css.heroInner}>
            <a href="/" className={css.backLink}>
              <span aria-hidden="true">←</span>
              Back to homepage
            </a>

            <p className={css.eyebrow}>{page.eyebrow}</p>
            <h1>{page.title}</h1>
            <p className={css.intro}>{page.intro}</p>
          {pageId === 'socal-rentals' ? <SoCalCountyExplorer /> : null}
        <ThrottleXPhase1DepositPolicyNotice pageId={pageId} />
        {pageId === 'faq' ? <ThrottleXFAQContent /> : null}
          {page.downloadHref ? (
            <a className={css.policyDownloadButton} href={page.downloadHref} download>
              {page.downloadLabel || 'Download checklist'}
            </a>
          ) : null}
          </div>
        </section>

        <section className={css.content}>
          <div className={css.contentIntro}>
            <span>ThrottleX policies</span>
            <h2>Important information for renters and hosts</h2>
            <p>
              Review each section carefully before creating a listing, making a reservation, or
              operating a rental.
            </p>
          </div>

          <div className={css.cardList}>
            {page.sections.map((section, index) => (
              <article key={section.title} className={css.card}>
                <div className={css.cardNumber}>
                  {String(index + 1).padStart(2, '0')}
                </div>

                <div className={css.cardContent}>
                  <h2>{section.title}</h2>
                  <p>{section.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <footer className={css.footer}>
          <div className={css.footerInner}>
            <div>
              <a href="/" className={css.footerBrand}>
                <span>Throttle</span>
                <strong>X</strong>
              </a>

              <p>
                Premium outdoor rentals for lake days, trails, dunes, and weekend adventures.
              </p>
            </div>

            <div className={css.footerLinks}>
              <a href="/p/terms">Terms</a>
              <a href="/p/privacy">Privacy</a>
              <a href="/p/liability">Liability</a>
              <a href="/p/insurance">Insurance</a>
              <a href="/p/safety-guidelines">Safety</a>
              <a href="/p/rental-rules">Rental rules</a>
              <a href="/p/host-standards">Host standards</a>
              <a href="/p/faq">FAQ</a>
              <a href="/p/socal-rentals">Search by county</a>
              <a href="/p/utv-renter-safety-checklist">UTV renter checklist</a>
              <a href="/p/contact">Contact</a>
            </div>
          </div>

          <div className={css.footerBottom}>
            <p>© {currentYear} ThrottleX. All rights reserved.</p>

            <div>
              <a href="/s">Browse rentals</a>
              <a href="/signup">Start hosting</a>
            </div>
          </div>
        </footer>
      </main>
    </Page>
  );
};

export default PolicyPage;
